// Package imports:
import React from 'react';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
import Link from '../../ui-elements/Link/Link';
import BulletList from '../../ui-elements/Lists/BulletList';
import KYCNavBar from '../Company/KYCNavBar/KYCNavBar';
import ScrollSpy from '../../ui-elements/ScrollSpy/ScrollSpy';
import ScrollSpySection from '../../ui-elements/ScrollSpy/ScrollSpySection';

/**
 * Instructions Component
 * 
 * This component displays comprehensive instructions for the Keldan KYC system.
 * It provides step-by-step guidance for conducting due diligence on both legal entities
 * and individuals. The component uses ScrollSpy for navigation, allowing users to easily
 * jump between different sections of the instructions.
 * 
 * The page is structured into two main sections:
 * 1. Due diligence for legal entities (Lögaðilar) - 4 steps
 * 2. Due diligence for individuals (Einstaklingar) - 3 steps
 * 
 * The navigation is now grouped with headers for each main section, making it easier
 * for users to understand the structure and navigate between different types of due diligence.
 */
const Instructions: React.FC = () => {
    // Bullet points for the "Hefja könnun" (Start survey) section for legal entities
    // Lists the automatic data collection and search features performed by the system
    const hefjakonnunBulletList: string[] = [
        "Sækir Gildandi skráningu úr Hlutafélagaskrá, þar á meðal upplýsingar um raunverulega eigendur.",
        "Birtir út frá skráningunni þá einstaklinga sem gegna hlutverkum innan lögaðilans. Prókúruhafar, raunverulegir eigendur og tengiliður (ef hann er ekki meðal fyrrnefndra) eru dregnir sérstaklega út til frekari vinnslu.",
        "Flettir upp einstaklingunum og birtir ríkisfang og búsetuland, og ber þessi gögn saman við gagnagrunn yfir áhættusöm og ósamvinnuþýð ríki.",
        "Ber þá saman við PEP-lista Keldunnar um stjórnmálaleg tengsl.",
        "Leitar á móti alþjóðlegum válistum, erlendum PEP-grunnum og neikvæðu orðspori."
    ];

    // Bullet points for foreign parties or those without electronic credentials
    // Outlines the alternative process for non-Icelandic users
    const erlendirAdilarBulletList: string[] = [
        "Tölvupóstur sem bæði er á íslensku og ensku inniheldur hlekk inn á spurningalista sem er virkur í tvær vikur.",
        "Hægt er að endursenda könnun til að endurvirkja hlekkinn.",
        "Spurningalisti er í boði á íslensku og ensku.",
        "Afrit af skilríkjum er hlaðið upp samhliða því að svara spurningum.",
    ];

    // Bullet points for the "Hefja könnun" section for individuals
    // Lists the automatic lookups performed for individual due diligence
    const hefjaKonnunEinstaklingarBulletList: string[] = [
        "Flettir upp ríkisfangi og búsetulandi, og ber saman við gagnagrunn yfir áhættusöm og ósamvinnuþýð ríki.",
        "Flettir upp í PEP-lista Keldunnar um stjórnmálaleg tengsl.",
        "Leitar á móti alþjóðlegum válistum, erlendum PEP-grunnum og neikvæðu orðspori."
    ];


    return (
        <div className="KCL_DueDiligence_instructionsPAGE">
            {/* Navigation bar component for KYC section */}
            <KYCNavBar />

            <div className="KCL_DueDiligence_instructions">
                <div className='mini_shell'>
                    {/* Header section with title and introductory summary */}
                    <HeadSection
                        title="Keldan KYC"
                        summary={
                            <p>
                                Keldan KYC kerfið er hannað fyrir tilkynningarskylda aðila til að framkvæma áreiðanleikakannanir og viðhafa eftirlit með viðskiptavinum sínum á skilvirkan hátt og í samræmi við gildandi lög.<br />
                                Ef spurningar vakna eða ef þú vilt koma ábendingum á framfæri er hægt að hafa samband á <Link linkSize='18' href='mailto:info@keldan.is'>info@keldan.is</Link>
                            </p>
                        }
                    />

                    {/* Main article container for the instructions */}
                    <article className="article-overview noPadding">
                        <div className="article__body">
                            {/* 
                                ScrollSpy component handles the navigation and content synchronization
                                - Tracks scroll position and updates navigation accordingly
                                - Provides smooth scrolling between sections
                                - Initial step is set to the first legal entity section
                                - Now supports grouped navigation with section headers
                            */}
                            <ScrollSpy
                                containerClass="article__content"
                                navClass="article__nav withPadding"
                                parentClass="article__body"
                                initialStep="logadilar-hefja-konnun"
                                grouped
                                autoIndexing={false}
                            >
                                {/* ==================== SECTION 1: LEGAL ENTITIES ==================== */}
                                {/* 
                                    Main heading for the legal entities section
                                    This heading appears in the content but not in the navigation
                                    The navigation groups are created via the 'group' prop on ScrollSpySection
                                */}
                                <ScrollSpySection
                                    id={'Logadilar'}
                                    className='navGrouper'
                                    label={'Áreiðanleikakönnun á lögaðilum'}
                                >
                                    <h2 className="article__main-heading">Áreiðanleikakönnun á lögaðilum</h2>

                                    <p className='paragraph'>
                                        Á svæðinu Áreiðanleikakannanir (KYC) í aðalvalmynd má nú sjá þá lögaðila sem hafa verið stofnaðir.
                                        Til að framkvæma aðgerðir þarf að haka í hvert félag, eitt eða fleiri, eftir því sem á við.
                                        Því næst er áreiðanleikakönnun framkvæmd í þeirri röð sem lýst er í leiðbeiningum á síðunni.
                                    </p>
                                </ScrollSpySection>
                                {/* 
                                    Step 1: Start Survey for Legal Entities
                                    The 'group' prop assigns this section to the "Legal Entities" navigation group
                                */}
                                <ScrollSpySection
                                    className="article__section"
                                    id="logadilar-hefja-konnun"
                                    label="1. Hefja könnun"
                                >
                                    <h3>1. Hefja könnun</h3>
                                    <p className='paragraph'>
                                        Fyrsta skref er að velja þann eða þá lögaðila sem hefja á áreiðanleikakönnun á og ýta á „Hefja könnun".
                                        Aðgerðinni fylgir sjálfvirk gagnaöflun og leit þar sem kerfið m.a.:
                                    </p>
                                    <BulletList items={hefjakonnunBulletList} />
                                </ScrollSpySection>

                                {/* Step 2: Register Contact Person for Legal Entities */}
                                <ScrollSpySection
                                    className="article__section"
                                    id="logadilar-skra-tengilid"
                                    label="2. Skrá tengilið"
                                >
                                    <h3>2. Skrá tengilið</h3>
                                    <p className='paragraph'>
                                        Næsta skref er að skrá tengilið, en sá verður að hafa lagalega heimild til að koma fram fyrir hönd lögaðilans.
                                        Í vali á tengilið eru sjálfkrafa þeir í boði sem heimild hafa skv. sóttri gildandi skráningu.
                                        Netfang þarf einnig að skrá. Þegar tengiliður hefur verið skráður virkjast áreiðanleikakönnun á kyc.keldan.is,
                                        og er aðgengileg viðkomandi við rafræna innskráningu. Þá er valkvætt að senda beiðni á aðra prókúruhafa sem
                                        skrá sig inn með rafrænum skilríkjum og sanna á sér deili.
                                    </p>
                                    <p className='paragraph'>
                                        <strong>Erlendir aðilar / ekki með rafræn skilríki:</strong><br />
                                        Ef tengiliður lögaðilans er erlendur eða aðili sem ekki hefur rafræn skilríki er hægt að velja "Annar"
                                        og haka við "Erlendur aðili / ekki með rafræn skilríki"
                                    </p>
                                </ScrollSpySection>

                                {/* Step 3: Send Survey for Legal Entities */}
                                <ScrollSpySection
                                    className="article__section"
                                    id="logadilar-senda-konnun"
                                    label="3. Senda könnun"
                                >
                                    <h3>3. Senda könnun</h3>
                                    <p className='paragraph'>
                                        Þriðja skref er að senda könnun á skráðan tengilið lögaðila. Tölvupóstur er sendur út í nafni tilkynningarskylds
                                        aðila með hlekk á könnun. Tengiliður skráir sig inn með rafrænum skilríkjum og svarar spurningalista.
                                        Þessa aðgerð má endurtaka og virkar hún þá sem áminning eða ítrekun. Eina og sama könnunin bíður viðkomandi
                                        í kerfinu óháð hversu oft könnun er send.
                                    </p>
                                    <p className='paragraph'>
                                        Tengiliður móttekur tölvupóst í nafni tilkynningarskylds aðila sem kemur frá{' '}
                                        <Link linkSize='18' href='mailto:noreply@keldan.is'>noreply@keldan.is</Link>.
                                        Athugið að hægt er að óska eftir sérstökri 'subdomain' slóð sem hægt er að tengja við aðgang notanda.
                                        Í tölvupóstinum er hlekkur inn á <Link linkSize='18' href="https://kyc.keldan.is">kyc.keldan.is</Link> þar
                                        sem tengiliður skráir sig inn með rafrænum skilríkjum og svarar áreiðanleikakönnun.
                                    </p>
                                    <p className='paragraph'>
                                        <strong>Erlendir aðilar / ekki með rafræn skilríki:</strong><br />
                                        <BulletList items={erlendirAdilarBulletList} />
                                    </p>
                                </ScrollSpySection>

                                {/* Step 4: Risk Assessment for Legal Entities */}
                                <ScrollSpySection
                                    className="article__section"
                                    id="logadilar-ahaettumat"
                                    label="4. Áhættumat skráð"
                                >
                                    <h3>4. Áhættumat skráð</h3>
                                    <p className='paragraph'>
                                        Fjórða skref er að skrá áhættumat handvirkt eða sjálfvirkt með litakóða ásamt athugasemdum eða greinargerð
                                        þegar öll gögn liggja fyrir, fyrir hvern viðskiptavin eða marga í einu. Vakin er athygli á því að sjálfvirkt
                                        áhættumat er einungis tillaga og því mikilvægt að fara vel yfir niðurstöður úr áreiðanleikakönnun.
                                        Til að virkja sjálfvirkt áhættumat þarf að haka í þá lögaðila sem hafa svarað könnun og velja sjálfvirkt áhættumat.
                                        Alltaf er hægt að uppfæra áhættumat handvirkt. Kerfið skráir hver uppfærði áhættumatið síðast og hvenær.
                                    </p>
                                    <p className='paragraph'>
                                        Hægt er að hlaða upp allt að 10 skjölum við niðurstöðu könnunar. Ef þörf er á aukinni áreiðanleikakönnun
                                        má hlaða upp viðbótargögnum og upplýsingum sem aflað er um viðskiptavininn á viðkomandi áreiðanleikakönnun.
                                    </p>
                                </ScrollSpySection>

                                {/* ==================== SECTION 2: INDIVIDUALS ==================== */}
                                {/* 
                                    Main heading for the individuals section
                                    This heading appears in the content but not in the navigation
                                    The navigation groups are created via the 'group' prop on ScrollSpySection
                                */}
                                <ScrollSpySection
                                    id={'Einstaklingar'}
                                    className='navGrouper'
                                    label={'Áreiðanleikakönnun á einstaklingum'}
                                >

                                    <h2 className="article__main-heading" style={{ marginTop: '60px' }}>Áreiðanleikakönnun á einstaklingum</h2>

                                    <p className='paragraph'>
                                        Framkvæmd áreiðanleikakönnunar á einstaklingum fer fram með svipuðu móti og á lögaðilum en í færri skrefum.
                                    </p>

                                </ScrollSpySection>
                                <ScrollSpySection
                                    className="article__section"
                                    id="einstaklingar-hefja-konnun"
                                    label="1. Hefja könnun"
                                >
                                    <h3>1. Hefja könnun</h3>
                                    <p className='paragraph'>
                                        Fyrsta skref er að velja þann eða þá einstaklinga sem hefja á áreiðanleikakönnun á og ýta á „Hefja könnun".
                                        Kerfið framkvæmir síðan sjálfvirkar uppflettingar:
                                    </p>
                                    <BulletList items={hefjaKonnunEinstaklingarBulletList} />
                                </ScrollSpySection>

                                {/* Step 2: Send Survey for Individuals */}
                                <ScrollSpySection
                                    className="article__section"
                                    id="einstaklingar-senda-konnun"
                                    label="2. Senda könnun"
                                >
                                    <h3>2. Senda könnun</h3>
                                    <p className='paragraph'>
                                        Annað skref er að senda áreiðanleikakönnun. Tölvupóstur er sendur út í nafni tilkynningarskylds aðila með
                                        hlekk á könnun. Einstaklingurinn skráir sig inn með rafrænum skilríkjum og svarar spurningalista.
                                        Þessa aðgerð má endurtaka og virkar hún þá sem áminning eða ítrekun. Eina og sama könnunin bíður viðkomandi
                                        í kerfinu óháð hversu oft könnun er sent.
                                    </p>
                                    <p className='paragraph'>
                                        Viðskiptavinur móttekur tölvupóst í nafni tilkynningarskylds aðila sem kemur frá{' '}
                                        <Link linkSize='18' href='mailto:noreply@keldan.is'>noreply@keldan.is</Link>.
                                        Athugið að hægt er að óska eftir sérstakri 'subdomain' slóð sem hægt er að tengja við aðgang notanda.
                                        Í tölvupóstinum er hlekkur inn á <Link linkSize='18' href="https://kyc.keldan.is">kyc.keldan.is</Link> þar
                                        sem tengiliður skráir sig inn með rafrænum skilríkjum og svarar áreiðanleikakönnun.
                                    </p>
                                    <p className='paragraph'>
                                        <strong>Erlendir aðilar / ekki með rafræn skilríki:</strong><br />
                                        <BulletList items={erlendirAdilarBulletList} />
                                    </p>
                                </ScrollSpySection>

                                {/* Step 3: Risk Assessment for Individuals */}
                                <ScrollSpySection
                                    className="article__section"
                                    id="einstaklingar-ahaettumat"
                                    label="3. Áhættumat skráð"
                                >
                                    <h3>3. Áhættumat skráð</h3>
                                    <p className='paragraph'>
                                        Þriðja skref er að skrá áhættumat handvirkt eða sjálfvirkt með litakóða ásamt athugasemdum eða greinargerð
                                        þegar öll gögn liggja fyrir, fyrir hvern viðskiptavin eða marga í einu. Vakin er athygli á því að sjálfvirkt
                                        áhættumat er einungis tillaga og því mikilvægt að fara vel yfir niðurstöður úr áreiðanleikakönnun.
                                        Til að virkja sjálfvirkt áhættumat þarf að haka í þá viðskiptavini sem hafa svarað könnun og velja sjálfvirkt áhættumat.
                                        Alltaf er hægt að uppfæra áhættumat handvirkt. Kerfið skráir hver uppfærði áhættumatið síðast og hvenær.
                                    </p>
                                    <p className='paragraph'>
                                        Hægt er að hlaða upp allt að 10 skjölum við niðurstöðu könnunar. Ef þörf er á aukinni áreiðanleikakönnun
                                        má hlaða upp viðbótargögnum og upplýsingum sem aflað er um viðskiptavininn á viðkomandi áreiðanleikakönnun.
                                    </p>
                                </ScrollSpySection>

                            </ScrollSpy>
                        </div>
                    </article>

                    {/* Footer contact information */}
                    <p className='paragraph' style={{ marginTop: '40px' }}>
                        Ef spurningar vakna eða ef þú vilt koma ábendingu á framfæri er hægt að hafa samband á{' '}
                        <Link linkSize='18' href='mailto:info@keldan.is'>info@keldan.is</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}

export default Instructions;